import fileDownload from "js-file-download";
import moment from "moment";
import { CSSProperties, ReactNode, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ReactZoomPanPinchRef } from "react-zoom-pan-pinch";
import {
  CCHS_CALL_CENTER_PHONE_NUMBER,
  Constants,
  ErrorConstants,
} from "../../constants";
import {
  EcgDetailsActionType,
  useEcgDetailsContext,
} from "../../context/EcgDetailsContext";
import {
  PaneProps,
  SplitContainerContextProvider,
} from "../../context/SplitContainerContext";
import useAlert from "../../hooks/useAlert";
import useAuthorizedAction, {
  AuthorizedActionsKeys,
} from "../../hooks/useAuthorizedAction";
import useResponsive from "../../hooks/useResponsive";
import AlertLevel from "../../models/alert";
import { IFormattedMessage } from "../../models/message.interface";
import {
  PostEcgSaveMessageCode,
  SaveInterpretedEcgFailureReason,
} from "../../models/results/save-interpreted-ecg.interface";
import ecgsService from "../../services/ecgs.service";
import {
  extractEnabledFromEdit,
  formatSelectedActionText,
  getActionHeader,
  getActionIcon,
  getActionTextColor,
  validateFindings,
} from "../../utils/ecgUtil";
import { generateFileName } from "../../utils/fileUtil";
import { round } from "../../utils/sharedUtil";
import AlertModal, { AlertModalProps } from "../Shared/AlertModal/AlertModal";
import Card from "../Shared/Card/Card";
import { Loader } from "@shltelemedicine/shl-ui-lib";
import PhoneNumber from "../Shared/PhoneNumber/PhoneNumber";
import SplitContainer from "../Shared/SplitContainer/SplitContainer";
import styles from "./EcgDetails.module.css";
import EcgDetailsFooter from "./EcgDetailsFooter/EcgDetailsFooter";
import EcgDetailsTopBar from "./EcgDetailsTopBar/EcgDetailsTopBar";
import AddNoteModal from "./EcgEdit/AddNoteModal/AddNoteModal";
import EcgEdit from "./EcgEdit/EcgEdit";
import Leads from "./EcgEdit/Leads/Leads";
import EcgMeta from "./EcgMeta/EcgMeta";
import EcgReviewLock from "./EcgReviewLock/EcgReviewLock";
import PostSaveInfoModal from "./PostSaveInfoModal/PostSaveInfoModal";
import SingleSaveWarningModal from "./UnreadableWarningModal/SingleSaveWarningModal";
import ArchiveInfoModal from "./ArchiveInfoModal/ArchiveInfoModal";
import EcgTransform from "./EcgTransform/EcgTransform";
import SendMailModal from "./EcgEdit/SendMailModal/SendMailModal";
import EcrOnDemandModal from "./EcrOnDemandModal/EcrOnDemandModal";
import {
  EcgReviewState,
  EcrOnDemandDataRejectReasons,
  EcrOnDemandDataStates,
  IEcg,
} from "../../models/ecg.interface";
import SuccessModal from "../Shared/SuccessModal/SuccessModal";

type EcgDetailsProps = {
  minGraphWidth?: number;
  onEcgUpdate: (reselectEcg?: boolean) => void;
  showEcgMetaOnMobile?: boolean;
  onToggleEcgMetaOnMobile?: (show: boolean) => void;
  onEcgReviewLock?: (ecgId: string | null) => void;
};

const getZoomFromScale = (scale: number) => {
  return round(scale, 1) * 100;
};

const BOTTOM_PANE_NO_EDIT_SIZE = 0;
const BOTTOM_PANE_SM_EDIT_SIZE = 15;
const BOTTOM_PANE_MD_EDIT_SIZE = 60;
const BOTTOM_PANE_LG_EDIT_SIZE = 76;

const BOTTOM_PANE_INIT_TABLET_PORTRAIT_SIZE = 32;
const BOTTOM_PANE_LEADS_SIZE = 23;
const BOTTOM_PANE_LEADS_MOBILE_SIZE = 30;

const BOTTOM_PANE_INIT_MOBILE_SIZE = 19;

const EcgDetails = ({
  showEcgMetaOnMobile,
  minGraphWidth,
  onEcgUpdate,
  onToggleEcgMetaOnMobile,
  onEcgReviewLock,
}: EcgDetailsProps) => {
  const {
    state: { selectedEcg, loading, saveFailed, draft, ecgEdit },
    dispatch,
    actions: { isEdited },
  } = useEcgDetailsContext();

  const canViewGraphAndData = useAuthorizedAction(
    AuthorizedActionsKeys.ViewEcgGraphAndData
  );

  const intl = useIntl();

  const isApproveMode = useAuthorizedAction(AuthorizedActionsKeys.ApproveEcg);
  const isReviewer = useAuthorizedAction(AuthorizedActionsKeys.ReviewEcg);

  const {
    showErrorAlert,
    showWarningAlert,
    alertProps,
    alertMessage,
    getAlertMessageAndSupportContent,
    getSupportData,
  } = useAlert();

  const [zoomPanRef, setZoomPanRef] = useState<ReactZoomPanPinchRef>();

  const [isEcgEditOpen, setIsEcgEditOpen] = useState(false);

  const [zoomLevel, setZoomLevel] = useState<number>(
    getZoomFromScale(Constants.MIN_ZOOM_SCALE)
  );

  const [showAddNoteModal, setShowAddNoteModal] = useState(false);

  const [showSendMailModal, setShowSendMailModal] = useState(false);
  const [showSendMailSuccessModal, setShowSendMailSuccessModal] = useState(
    false
  );

  const [showSingleSaveWarning, setShowSingleSaveWarning] = useState(false);

  const [showZoomInfo, setShowZoomInfo] = useState(false);

  const [bottomPaneInitSize, setBottomPaneInitSize] = useState(
    BOTTOM_PANE_NO_EDIT_SIZE
  );

  const [bottomPaneMaxSize, setBottomPaneMaxSize] = useState(
    bottomPaneInitSize
  );

  const [leadsTitle, setLeadsTitle] = useState<ReactNode>(<></>);

  const [findingKey, setFindingKey] = useState<string>("");

  const [showLeadsPane, setShowLeadsPane] = useState(false);

  const [showPostSaveModal, setShowPostSaveModal] = useState(false);

  const [showArchiveModal, setShowArchiveModal] = useState(false);

  const [showEcrOnDemandModal, setShowEcrOnDemandModal] = useState(false);
  const [showEcrOnDemandSentModal, setShowEcrOnDemandSentModal] = useState(
    false
  );
  const [
    showEcrOnDemandFailureModal,
    setShowEcrOnDemandFailureModal,
  ] = useState(false);

  const [postSaveInfoMessage, setPostSaveInfoMessage] = useState<ReactNode>();

  const { isMobile, isTabletPortrait } = useResponsive();

  const recalculatePanesSizes = () => {
    let bottomPaneMaxSize = BOTTOM_PANE_NO_EDIT_SIZE;
    if (!canEdit) {
      setBottomPaneInitSize(BOTTOM_PANE_NO_EDIT_SIZE);
    } else {
      setBottomPaneInitSize(BOTTOM_PANE_SM_EDIT_SIZE);
      bottomPaneMaxSize = BOTTOM_PANE_SM_EDIT_SIZE;

      if (showLeadsPane) {
        if (isMobile) {
          bottomPaneMaxSize = BOTTOM_PANE_LEADS_MOBILE_SIZE;
        } else {
          bottomPaneMaxSize = BOTTOM_PANE_LEADS_SIZE;
        }
        setBottomPaneInitSize(bottomPaneMaxSize);
      } else if (enableActions) {
        bottomPaneMaxSize = BOTTOM_PANE_LG_EDIT_SIZE;
      } else {
        bottomPaneMaxSize = BOTTOM_PANE_MD_EDIT_SIZE;
      }
    }
    setBottomPaneMaxSize(bottomPaneMaxSize);
  };

  useEffect(() => {
    zoomPanRef?.resetTransform(0);
    if (!selectedEcg?.permissions) {
      setBottomPaneInitSize(0);
      setBottomPaneMaxSize(0);
      return;
    }

    setShowLeadsPane(false);
    recalculatePanesSizes();
  }, [selectedEcg?.id]); //eslint-disable-line

  useEffect(() => {
    if (zoomPanRef != null) {
      setZoomLevel(getZoomFromScale(zoomPanRef?.state.scale));
    }
  }, [zoomPanRef]);

  useEffect(() => {
    recalculatePanesSizes();
  }, [showLeadsPane]); // eslint-disable-line

  const {
    enableActions,
    enableFindings,
    enableInterpretation,
    enablePriority,
    findingsRequired,
    interpretationRequired,
  } = selectedEcg?.permissions ?? {};

  const canEdit =
    (enableActions && enableInterpretation) || // Actions are editable only inside interpretaions
    enableInterpretation ||
    enableFindings ||
    enablePriority;

  const saveHandler = async () => {
    // Validate edit only if ECG is Readable
    if (ecgEdit?.isReadable === true) {
      const { isValid, invalidFields } = isEditValid();

      if (!isValid) {
        const errorMessage = getInvalidEditMessage(invalidFields);
        const description = getInvalidEditMessageText(invalidFields);
        const support = getSupportData({ description });
        showWarningAlert(errorMessage, support);
        return;
      }
    }

    if (selectedEcg?.saveOnce) {
      handleShowSingleSaveWarningModal();
      return;
    }

    onSaveApproved();
  };

  const ecrOnDemandHandler = async () => {
    dispatch({
      type: "SET_LOADING",
      payload: true,
    });

    if (!selectedEcg) return;
    try {
      const { success, errorCode } = await ecgsService.requestEcrOnDemand(
        selectedEcg.id
      );
      if (!success) {
        if (errorCode === ErrorConstants.ECG_SENT_FOR_REVIEW) {
          setShowEcrOnDemandFailureModal(true);
        } else {
          const { message, support } = getAlertMessageAndSupportContent(
            "ERROR.SOMETHING_WENT_WRONG",
            "Something went wrong",
            undefined,
            errorCode
          );
          showErrorAlert(message, support);
        }
        if (onEcgUpdate) onEcgUpdate();
      } else {
        setShowEcrOnDemandSentModal(true);
        const ecgInReview = {
          ...selectedEcg,
          ecgReviewState: EcgReviewState.ECRReviewInProcess,
          permissions: {
            ...selectedEcg?.permissions,
            canRequestECROnDemand: {
              state: EcrOnDemandDataStates.Disabled,
              rejectReason: EcrOnDemandDataRejectReasons.EcrReviewInProcess,
            },
          },
        } as IEcg;
        dispatch({
          type: EcgDetailsActionType.SET_SELECTED_ECG_DETAILS,
          payload: ecgInReview,
        });
      }
    } finally {
      dispatch({
        type: "SET_LOADING",
        payload: false,
      });

      setShowEcrOnDemandModal(false);
    }
  };

  const onSaveApproved = async () => {
    dispatch({
      type: "SET_LOADING",
      payload: true,
    });

    dispatch({
      type: "SET_SAVE_FAILED",
      payload: false,
    });

    try {
      const { saveOnce, showSuggestiveOf, ...enableFlags } =
        selectedEcg?.permissions ?? {};

      const editedEcg = extractEnabledFromEdit(ecgEdit!, enableFlags);

      const {
        success,
        failReason,
        errorCode,
        postSaveMessageCode,
      } = await ecgsService.updateEcg({
        id: selectedEcg?.id ?? "",
        ...editedEcg,
      });

      dispatch({
        type: "SET_SAVE_FAILED",
        payload: !success,
      });

      if (!success) {
        if (errorCode === ErrorConstants.ECG_SENT_FOR_REVIEW) {
          setShowEcrOnDemandFailureModal(true);
          if (onEcgUpdate) onEcgUpdate();
          return;
        }

        const { id, defaultMessage, values } = getSaveInterpretedEcgFailureMsg(
          failReason
        );
        const { message, support } = getAlertMessageAndSupportContent(
          id,
          defaultMessage,
          values,
          errorCode
        );
        showErrorAlert(message, support);
        handleCloseSingleSaveWarningModal();
        return;
      }

      dispatch({
        type: "SET_DRAFT",
        payload: {
          ...draft,
          mode: "read",
        },
      });

      if (postSaveMessageCode) {
        const postSaveMessage = getPostEcgMsg(postSaveMessageCode);
        setPostSaveInfoMessage(postSaveMessage);
        setShowPostSaveModal(true);
        return;
      }

      if (onEcgUpdate) onEcgUpdate();
    } finally {
      handleCloseSingleSaveWarningModal();

      dispatch({
        type: "SET_LOADING",
        payload: false,
      });

      window.location.reload();
    }
  };

  const downloadHandler = async () => {
    dispatch({
      type: "SET_LOADING",
      payload: true,
    });

    try {
      const {
        data: pdfFile,
        success,
        errorCode,
      } = await ecgsService.downloadPdf(selectedEcg!.id);

      if (!success || !pdfFile) {
        const { message, support } = getAlertMessageAndSupportContent(
          "ERROR.DOWNLOADING_REPORT",
          "An error occurred while downloading the report.<br></br>Please try again.",
          { br: () => <br></br> },
          errorCode
        );
        showErrorAlert(message, support);
        return;
      }

      const fileName = generateFileName({
        prefix: "ECG",
        content: [
          selectedEcg?.patient?.lastName ?? "",
          selectedEcg?.patient?.firstName ?? "",
        ],
        postfix: moment(selectedEcg?.date).format(Constants.DATE_FORMAT),
        fileExtension: "pdf",
      });

      fileDownload(pdfFile, fileName);
    } finally {
      dispatch({
        type: "SET_LOADING",
        payload: false,
      });
    }
  };

  const approveHandler = async () => {
    dispatch({
      type: "SET_LOADING",
      payload: true,
    });

    dispatch({
      type: "SET_SAVE_FAILED",
      payload: false,
    });

    try {
      const { success, errorCode } = await ecgsService.approveEcg(
        selectedEcg?.id ?? ""
      );

      dispatch({
        type: "SET_SAVE_FAILED",
        payload: !success,
      });

      if (!success) {
        const { message, support } = getAlertMessageAndSupportContent(
          "ERROR.REVIEWING_ECG",
          "An error occurred while marking ECG as reviewed.<br></br>Please try again.",
          {
            br: () => <br></br>,
          },
          errorCode
        );
        showErrorAlert(message, support);
        return;
      }

      if (onEcgUpdate) onEcgUpdate();
    } finally {
      dispatch({
        type: "SET_LOADING",
        payload: false,
      });
    }
  };

  const onShowArchiveModal = () => setShowArchiveModal(true);

  const onCloseArchiveModal = () => setShowArchiveModal(false);

  const onCloseSendMailSuccessModal = () => setShowSendMailSuccessModal(false);

  const onCloseEcrOnDemandModal = () => setShowEcrOnDemandModal(false);
  const onCloseEcrOnDemandSentModal = () => setShowEcrOnDemandSentModal(false);
  const onCloseEcrOnDemandFailureModal = () =>
    setShowEcrOnDemandFailureModal(false);

  const archiveHandler = async () => {
    onCloseArchiveModal();
    if (!selectedEcg?.id) return;

    const { success } = await ecgsService.addArchivingEcg(selectedEcg.id);

    if (!success) {
      showErrorAlert(
        <FormattedMessage
          id="ERROR.ECG.ARCHIVING"
          defaultMessage="An error occurred while archiving ECG."
        />
      );
      return;
    }
    if (onEcgUpdate) onEcgUpdate();
  };

  const onShowAddNoteModal = () => setShowAddNoteModal(true);

  const onCloseAddNoteModal = async () => setShowAddNoteModal(false);

  const noteAddedHandler = async () => {
    onCloseAddNoteModal();
    if (!selectedEcg?.id) {
      return;
    }

    const { success, data: updatedNotes } = await ecgsService.getNotes(
      selectedEcg.id
    );

    if (!success || !updatedNotes) {
      showErrorAlert(
        <FormattedMessage
          id="ERROR.ECG.LOADING_NOTES"
          defaultMessage="An error occurred while loading ECG notes."
        />
      );
      return;
    }

    dispatch({
      type: EcgDetailsActionType.SET_SELECTED_ECG_DETAILS,
      payload: {
        ...selectedEcg,
        notes: updatedNotes,
      },
    });
  };

  const onShowSendMailModal = () => setShowSendMailModal(true);

  const onCloseSendMailModal = async () => setShowSendMailModal(false);

  const onShowEcgOnDemandModal = () => setShowEcrOnDemandModal(true);

  const onSendHandler = async (emails: string[]) => {
    try {
      const { success } = await ecgsService.sendEcgByMail(
        selectedEcg?.id ?? "",
        emails
      );

      if (!success) {
        showErrorAlert(
          <FormattedMessage
            id="ERROR.ECG.SEND_MAIL"
            defaultMessage="An error occurred while sending ECG."
          />
        );
        return;
      } else {
        setShowSendMailSuccessModal(true);
      }
    } finally {
      onCloseSendMailModal();
    }
  };

  const isEditValid = (): {
    isValid: boolean;
    invalidFields: IFormattedMessage[];
  } => {
    const isInterpretationExists = !!ecgEdit?.interpretation;
    /* In case of multiple actions required are on the ecg - validate that at least one action is checked by identifying at least on truthy value.
      In case of one action required is on the ecg - validate that one option (Y/N) is selected by checking its value is not 'undefined'. */
    const isActionsExists =
      ecgEdit?.actions && ecgEdit?.actions?.length > 1
        ? !!ecgEdit?.actions?.filter((f) => f.value === true).length
        : ecgEdit?.actions?.length === 1 &&
          ecgEdit?.actions[0].value !== undefined;

    let invalidFields: IFormattedMessage[] = [];

    if (findingsRequired) {
      const invalidFindings = validateFindings(ecgEdit?.findings);

      if (invalidFindings.length) {
        invalidFields = [...invalidFields, ...invalidFindings];
      }
    }

    if (interpretationRequired && !isInterpretationExists)
      invalidFields.push({
        id: "ECG_DETAILS.INTERPRETATION",
      });
    if (enableActions && !isActionsExists)
      invalidFields.push({
        id: "ECG_DETAILS.ACTION_REQUIRED",
      });

    return { isValid: !!!invalidFields.length, invalidFields };
  };

  const getInvalidEditMessage = (
    invalidFields: IFormattedMessage[]
  ): ReactNode => {
    return (
      <div className="text-start">
        <FormattedMessage
          id="ERROR.EDIT_ECG_REQUIRED_FIELDS"
          defaultMessage={
            "An error occurred while trying to save an ECG review due to missing information.<br></br>Please fill in the following fields"
          }
          values={{
            br: () => <br></br>,
          }}
        />
        :
        {invalidFields.map((field) => (
          <>
            <br />
            -&nbsp;
            <FormattedMessage id={field.id} values={field.values} />
          </>
        ))}
      </div>
    );
  };

  const handleShowLeadsPane = (findingKey: string, findingName: string) => {
    setFindingKey(findingKey);
    setLeadsTitle(findingName);
    setShowLeadsPane(true);
  };

  const handleCloseLeadsPane = () => {
    setShowLeadsPane(false);
  };

  const getInvalidEditMessageText = (
    invalidFields: IFormattedMessage[]
  ): string => {
    const invalidMessage = `${intl.formatMessage(
      {
        id: "ERROR.EDIT_ECG_REQUIRED_FIELDS",
        defaultMessage:
          "An error occurred while trying to save an ECG review due to missing information.<br></br>Please fill in the following fields",
      },
      {
        br: () => " ",
      }
    )}:`;

    const invalidFieldsMessages: string[] = [];
    invalidFields.forEach((field) => {
      const fieldMessage = intl.formatMessage({ id: field.id }, field.values);
      invalidFieldsMessages.push(fieldMessage);
    });

    const invalidFieldsMessage = invalidFieldsMessages.join(", ");

    return `${invalidMessage} ${invalidFieldsMessage}.`;
  };

  const handleShowSingleSaveWarningModal = () => setShowSingleSaveWarning(true);

  const handleCloseSingleSaveWarningModal = () =>
    setShowSingleSaveWarning(false);

  const handleClosePostSaveInfoModal = () => {
    setShowPostSaveModal(false);
    if (onEcgUpdate) onEcgUpdate();
  };

  const graphMouseEnterHandler = () => setShowZoomInfo(true);
  const graphMouseLeaveHandler = () => setShowZoomInfo(false);

  const paneToggleHandler = () => setIsEcgEditOpen(!isEcgEditOpen);

  const detailsDynamicStyle: CSSProperties = minGraphWidth
    ? {
        width: `${showEcgMetaOnMobile ? 0 : minGraphWidth}vw`,
        minWidth: `${showEcgMetaOnMobile ? 0 : minGraphWidth}vw`,
      }
    : {};

  const handleReviewConfirmation = () => {
    setShowReviewedConfirmModal(false);
    approveHandler();
  };

  const [showReviewedConfirmModal, setShowReviewedConfirmModal] = useState(
    false
  );

  const saveEnabled = !loading && selectedEcg && canEdit && isEdited();

  const panes: PaneProps[] = [
    {
      initialSize: 100 - bottomPaneInitSize,
      minSize: 100 - bottomPaneMaxSize,
      maxSize: 100 - bottomPaneInitSize,
      content: !canViewGraphAndData ? (
        <NotPermitted />
      ) : (
        <div
          className="d-flex flex-column h-100"
          onMouseEnter={graphMouseEnterHandler}
          onMouseLeave={graphMouseLeaveHandler}
        >
          <EcgDetailsTopBar
            zoomLevel={zoomLevel}
            minZoom={getZoomFromScale(Constants.MIN_ZOOM_SCALE)}
            maxZoom={getZoomFromScale(Constants.MAX_ZOOM_SCALE)}
            onZoomIn={() => {
              zoomPanRef?.zoomIn(Constants.ZOOM_SCALE_STEP, 0);
            }}
            onZoomOut={() => {
              zoomPanRef?.zoomOut(Constants.ZOOM_SCALE_STEP, 0);
            }}
            showZoomInfo={!loading && !!selectedEcg && showZoomInfo}
            onShowEcgMeta={() =>
              onToggleEcgMetaOnMobile && onToggleEcgMetaOnMobile(true)
            }
          />
          <div className={`${styles.graphWrapper} flex-grow-1 overflow-hidden`}>
            {loading && <Loader />}
            {selectedEcg && selectedEcg.graph && (
              <EcgTransform
                graph={selectedEcg.graph}
                setZoomPanRef={setZoomPanRef}
              />
            )}
          </div>
        </div>
      ),
    },
    {
      initialSize: bottomPaneInitSize,
      minSize: isMobile
        ? BOTTOM_PANE_INIT_MOBILE_SIZE
        : isTabletPortrait
        ? BOTTOM_PANE_INIT_TABLET_PORTRAIT_SIZE
        : bottomPaneInitSize,
      maxSize: bottomPaneMaxSize,
      showToggle: true,
      isCollapsible:
        selectedEcg && bottomPaneMaxSize !== bottomPaneInitSize ? true : false,
      togglePosition: "top",
      content: !showLeadsPane ? (
        <EcgEdit
          isCollapsed={!isEcgEditOpen}
          showLeadsPane={handleShowLeadsPane}
          setPaneState={paneToggleHandler}
        />
      ) : (
        <div className="h-100 w-100 d-flex flex-column">
          <Leads
            title={leadsTitle}
            closeLeadsPane={handleCloseLeadsPane}
            findingKey={findingKey}
          />
        </div>
      ),
    },
  ];

  const ecgReviewLockHandler = () => {
    if (onEcgReviewLock) onEcgReviewLock(selectedEcg!.id);
  };

  const ecgReviewTimeoutHandler = () => {
    // if (onEcgUpdate) onEcgUpdate(false);
    if (onEcgReviewLock) onEcgReviewLock(null);
  };

  const getSelectedActionComponent = () => {
    const selectedAction = ecgEdit?.actions?.find((f) => f.value);
    if (!selectedAction) return null;
    const { linesOfBoldText, linesOfRegularText } = formatSelectedActionText(
      selectedAction.displayName
    );
    return (
      <div>
        <h5
          className={`d-flex align-items-center justify-content-center capitalized ${styles.selectedActionTextBold} ${styles.selectedActionSectionsMargin}`}
          style={{ color: getActionTextColor(selectedAction.severity) }}
        >
          <img
            className={styles.selectedActionIcon}
            src={getActionIcon(selectedAction.severity)}
            alt={getActionHeader(selectedAction.severity)}
          />{" "}
          {getActionHeader(selectedAction.severity)}
        </h5>
        <div className={styles.selectedActionSectionsMargin}>
          {linesOfBoldText.map((line, index) => (
            <p className={styles.selectedActionTextBold} key={index}>
              {line}
            </p>
          ))}
        </div>
        <div className={styles.selectedActionSectionsMargin}>
          {linesOfRegularText.map((line, index) => (
            <p className={styles.selectedActionText} key={index}>
              {line}
            </p>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="d-flex h-100">
      <div
        style={detailsDynamicStyle}
        className={`${styles.detailsWrapper} d-flex flex-column h-100`}
      >
        <SplitContainerContextProvider panes={panes}>
          <SplitContainer
            type="horizontal"
            isResize={false}
            onTogglePane={paneToggleHandler}
          />
        </SplitContainerContextProvider>

        {!showEcgMetaOnMobile && !showLeadsPane && (
          <div className="border-top">
            <EcgDetailsFooter
              mode={isApproveMode ? "approve" : "save"}
              disabled={!selectedEcg || loading}
              saveFailed={saveFailed}
              saveDisabled={!saveEnabled}
              hideSave={!canEdit}
              hideApprove={!selectedEcg?.permissions?.enableApprove}
              enableNotes={selectedEcg?.permissions?.enableNotes}
              enableArchiving={
                selectedEcg?.permissions?.archiving.canBeArchived
              }
              hideArchiving={selectedEcg?.isArchived}
              enableSendEmail={selectedEcg?.permissions?.canSendEcgsViaMail}
              onSave={!isApproveMode ? saveHandler : undefined}
              onDownload={downloadHandler}
              onApprove={
                isApproveMode
                  ? () => setShowReviewedConfirmModal(true)
                  : undefined
              }
              onAddNote={onShowAddNoteModal}
              onArchive={onShowArchiveModal}
              onShare={onShowSendMailModal}
              onEcrOnDemand={onShowEcgOnDemandModal}
              ecrOnDemandData={selectedEcg?.permissions?.canRequestECROnDemand}
            />
          </div>
        )}
        {showReviewedConfirmModal && (
          <ReviewedConfirmationModal
            dialog
            show={showReviewedConfirmModal}
            okBtn={{
              onClick: handleReviewConfirmation,
              text: <FormattedMessage id="GLOBAL.YES" defaultMessage="Yes" />,
            }}
            cancelBtn={{
              text: <FormattedMessage id="GLOBAL.NO" defaultMessage="No" />,
            }}
            onClose={() => setShowReviewedConfirmModal(false)}
          />
        )}
      </div>
      <div className={`${styles.metaWrapper} app-border flex-grow-1`}>
        {!canViewGraphAndData ? <NotPermitted /> : <EcgMeta />}
      </div>

      <AddNoteModal
        show={showAddNoteModal}
        onNoteAdded={noteAddedHandler}
        onClose={onCloseAddNoteModal}
      />

      <SendMailModal
        show={showSendMailModal}
        onSend={onSendHandler}
        onClose={onCloseSendMailModal}
      />
      <SuccessModal
        onClose={onCloseSendMailSuccessModal}
        show={showSendMailSuccessModal}
        title={
          <FormattedMessage
            id="ECG_DETAILS.SEND_VIA_EMAIL.SUCCESS_TITLE"
            defaultMessage="ECG Sent"
          />
        }
        message={
          <FormattedMessage
            id="ECG_DETAILS.SEND_VIA_EMAIL.SUCCESS_MESSAGE"
            defaultMessage="Your ECG has been sent"
          />
        }
      />

      <SingleSaveWarningModal
        show={showSingleSaveWarning}
        saving={loading}
        onClose={handleCloseSingleSaveWarningModal}
        onSave={onSaveApproved}
        selectedActionComponent={getSelectedActionComponent()}
      />

      <PostSaveInfoModal
        show={showPostSaveModal}
        message={postSaveInfoMessage}
        onClose={handleClosePostSaveInfoModal}
      />

      <ArchiveInfoModal
        show={showArchiveModal}
        onClose={onCloseArchiveModal}
        onArchive={archiveHandler}
        isReviewCompleted={
          selectedEcg?.permissions?.archiving.isReviewCompleted
        }
      />

      <EcrOnDemandModal
        show={showEcrOnDemandModal}
        onClose={onCloseEcrOnDemandModal}
        onAgree={ecrOnDemandHandler}
      />
      <SuccessModal
        onClose={onCloseEcrOnDemandSentModal}
        show={showEcrOnDemandSentModal}
        title={
          <FormattedMessage
            id="ECG_DETAILS.ECRONDEMAND.SUCCESS_TITLE"
            defaultMessage="ECG Sent"
          />
        }
        message={
          <FormattedMessage
            id="ECG_DETAILS.ECRONDEMAND.SUCCESS_MESSAGE"
            defaultMessage="Your ECG was sent for review."
          />
        }
      />
      <SuccessModal
        onClose={onCloseEcrOnDemandFailureModal}
        show={showEcrOnDemandFailureModal}
        showIcon={false}
        title={
          <FormattedMessage
            id="ALERT_MODAL.ERROR.TITLE"
            defaultMessage="Please Note"
          />
        }
        message={
          <FormattedMessage
            id="ECG_DETAILS.ECRONDEMAND.ALREADY_SENT_FAILURE_MESSAGE"
            defaultMessage={
              "This ECG has already been sent for a Cardiologist Review. Please wait to view the results."
            }
          />
        }
      />

      {isReviewer && selectedEcg?.timeout && (
        <EcgReviewLock
          ecgId={selectedEcg.id}
          timeoutInMilliseconds={selectedEcg.timeout}
          onLock={ecgReviewLockHandler}
          onLockTimeout={ecgReviewTimeoutHandler}
        />
      )}
      <AlertModal {...alertProps}>{alertMessage}</AlertModal>
    </div>
  );
};

export default EcgDetails;

const NotPermitted = () => {
  return (
    <div className="h-100 w-100 position-relative">
      <Card className="app-shadow absolute-center w-75 text-center">
        <h3 className="m-0 p-2">N/A</h3>
      </Card>
    </div>
  );
};

const ReviewedConfirmationModal = (props: AlertModalProps) => {
  return (
    <AlertModal level={AlertLevel.Info} {...props}>
      <FormattedMessage
        id="ECG_DETAILS.REVIEWED.WARNING"
        defaultMessage="By pressing the PI Review button, you are attesting that you have reviewed this ECG. This action is final and cannot be changed.<br></br>Continue?"
        values={{
          br: () => <br></br>,
        }}
      />
    </AlertModal>
  );
};

const getSaveInterpretedEcgFailureMsg = (
  failureReason: SaveInterpretedEcgFailureReason | undefined
): IFormattedMessage => {
  if (!failureReason) return {} as IFormattedMessage;

  switch (failureReason) {
    case SaveInterpretedEcgFailureReason.EcgAlreadySaved:
      return {
        id: "ERROR.ALREADY_SAVED_ECG",
        defaultMessage: "The ECG has been already saved by another reviewer.",
      };
    default:
      return {
        id: "ERROR.SAVE_ECG",
        defaultMessage:
          "An error occurred while trying to save the ECG.<br></br>Please try again.",
        values: {
          br: () => <br></br>,
        },
      };
  }
};

const getPostEcgMsg = (
  postEcgSaveMessageCode: PostEcgSaveMessageCode
): ReactNode => {
  switch (postEcgSaveMessageCode) {
    case PostEcgSaveMessageCode.EcgMarkedCritical:
      return (
        <FormattedMessage
          id="INFO.POST_SAVE_ECG"
          defaultMessage="This ECG has been marked critical, please call CCHS Call Center @ {phoneNumber} ASAP"
          values={{
            phoneNumber: <PhoneNumber number={CCHS_CALL_CENTER_PHONE_NUMBER} />,
          }}
        />
      );
    default:
      return;
  }
};
